import { useChangeSeatMutation, useMyWithoutRedirect } from 'features/my/my.hook';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { getDate00 } from 'utils/dateUtil';
import { useToast } from './useToast';
import { useTranslation } from 'react-i18next';
import { MyBookItem } from 'features/my/my';
import { useAppDispatch, useAppSelector } from 'store/useAppStore';
import { bookActions, selectBookingAuthenticated } from 'store/slices/book';
import { useNavigate } from 'react-router';

/**
 * 좌석 상태를 관리한다.
 * @param pickDate 선택한 날짜
 * @param unavailableSeats 해당 날짜에 가능하지 않은 좌석들
 * @returns 
 */
export const useSeat = (pickDate: Date, unavailableSeats: Set<number> | undefined) => {
    const { color } = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const toast = useToast();
    const { t } = useTranslation();
    const { mutate: changeSeats } = useChangeSeatMutation(getDate00(pickDate));
    const { token, authenticated } = useAppSelector(state => selectBookingAuthenticated(state));

    const [selectedSeats, setSelectedSeat] = useState<Record<number, boolean>>({});
    const [targetToMoveSeats, setTargetToMoveSeat] = useState<number[]>([]);
    const [isChangeSeatMode, setIsChangeSeatMode] = useState(false);

    // 마이데이터
    const { data: myReservations } = useMyWithoutRedirect();
    const mySeats = myReservations
        ? myReservations.filter(x => x[0] === getDate00(pickDate).toJSON()).flatMap(x => x[1]) || []
        : [];
    const mySeatIds = mySeats.map(x => x.seatId);
    const mySeatIdMapper: { [key: number]: MyBookItem } = mySeats.reduce((acc, cur) => {
        return { ...acc, [cur.seatId]: cur };
    }, {});

    /** 좌석변경 */
    const handleOnChangeSeat = () => {
        const selectedSeatIds = Object.keys(selectedSeats).map(seat => seat + '');
        const prevSeatReservationIds = targetToMoveSeats.map(t => Number(mySeatIdMapper[t].reservationId));

        if (prevSeatReservationIds.length <= 0 || selectedSeatIds.length <= 0) {
            toast.notifyAlert(t('좌석선택이안됐어요'));
            return;
        }

        if (prevSeatReservationIds.length !== selectedSeatIds.length) {
            toast.notifyAlert(t('좌석개수매칭안됨'));
            return;
        }

        if (!token || !authenticated) {
            toast.notifyAlert(t('토큰만료됨'));
            dispatch(bookActions.setPayInfo({ payType: 'CHANGE_SEAT' }));
            navigate('/auth');
            return;
        }

        const reservations = [...Array(selectedSeatIds.length).keys()].map(k => ({
            id: prevSeatReservationIds[k],
            seatId: selectedSeatIds[k]
        }))

        changeSeats({ token, reservations }, {
            onSuccess: () => {
                setIsChangeSeatMode(false);
                toast.notifySuccess(t('자리변경성공'))
                setSelectedSeat({});
                setTargetToMoveSeat([]);
            }
        });
    }

    /** 좌석 선택 버튼 핸들러 */
    const handleOnClickSeat = (seatNumber: number) => {
        if (isChangeSeatMode === false) {
            onClickSeatForBookSeat(seatNumber);
            return;
        }

        onClickSeatForChangeSeat(seatNumber);
    }

    /** 좌석 선택 : 좌석변경모드 */
    const onClickSeatForChangeSeat = (seatNumber: number) => {
        if (mySeatIds.includes(seatNumber)) {
            if (targetToMoveSeats.includes(seatNumber)) {
                const removed = targetToMoveSeats.filter(c => c !== seatNumber);
                setTargetToMoveSeat(removed);
                return;
            }
            setTargetToMoveSeat([...targetToMoveSeats, seatNumber]);
            return;
        }

        if (unavailableSeats?.has(seatNumber)) {
            return;
        }

        if (selectedSeats[seatNumber]) {
            const { [seatNumber]: _, ...others } = selectedSeats;
            setSelectedSeat(others);
            return;
        }
        setSelectedSeat({ ...selectedSeats, [seatNumber]: true });
    }

    /** 좌석 선택 : 좌석예약모드 */
    const onClickSeatForBookSeat = (seatNumber: number) => {
        const isSelected = selectedSeats[seatNumber];
        if (unavailableSeats?.has(seatNumber) && !isSelected) {
            return;
        }
        if (isSelected) {
            const { [seatNumber]: _, ...others } = selectedSeats;
            setSelectedSeat(others);
            return;
        }
        setSelectedSeat({ ...selectedSeats, [seatNumber]: true });
    }

    /** 내 좌석보다, 좌석변경을 위해 선택한 좌석이 많지 않도록 한다. */
    const ensureSelectedChangeSeatNumber = () => {
        const mySeatIdsLength = mySeatIds.length;
        const n = Object.keys(selectedSeats).length - mySeatIdsLength;

        const keys = Object.keys(selectedSeats).map(x => Number(x));
        for (let i = 0; i < n; i++) {
            delete selectedSeats[keys[i]];
        }
        setSelectedSeat(selectedSeats);
    }

    /** 좌석변경모드 <-> 예약모드 */
    const handleChangeMode = () => {
        setIsChangeSeatMode(true);
        ensureSelectedChangeSeatNumber();
        const numberOfSelectedSeats = Object.keys(selectedSeats).length;
        const changeSeatTargets = mySeatIds.slice(0, numberOfSelectedSeats);
        setTargetToMoveSeat(changeSeatTargets);
    }

    /** 좌석 색깔 결정 */
    const colorSelector = (seatNumber: number) => {
        const targetToMoveSeat = isChangeSeatMode && targetToMoveSeats.includes(seatNumber);
        const mySeat = mySeatIds.includes(seatNumber);
        const unableSeat = unavailableSeats?.has(seatNumber);
        const selected = !!selectedSeats[seatNumber];

        if (targetToMoveSeat) {
            return { fgColor: color.grey100, bgColor: color.focus300 };
        }

        if (mySeat) {
            return { fgColor: color.grey100, bgColor: color.focus200 };
        }

        if (unableSeat) {
            return { fgColor: color.grey700, bgColor: color.grey700 };
        }

        if (selected) {
            return { fgColor: color.grey100, bgColor: color.secondary900 };
        }

        return { fgColor: color.grey700, bgColor: color.grey100 }
    }

    return {
        state: { mySeatIds, isChangeSeatMode, targetToMoveSeats, selectedSeats },
        handler: {
            handleChangeMode, handleOnClickSeat, handleOnChangeSeat
        },
        action: {
            colorSelector, setIsChangeSeatMode, setSelectedSeat
        }
    }
}